// frontend/src/askos/pyq/PyqPracticePage.js
//
// Student PYQ practice: filter, answer, reveal explanation, track accuracy.
// Answer/explanation are withheld by the backend until attempted.
//
// Filtering is layered: Exam Type (a broad upload-time category like
// "Optional Subjects") -> Subject -> Historical/Predicted -> (when
// Historical) Year + specific Paper. PYQQuestion.paper itself holds the
// specific paper title (e.g. "PSIR Paper I"), which the backend resolves
// from exam_type via its upload lineage, not a plain equality match. Year/
// specific-paper narrowing happens client-side against the already-
// fetched set (capped at 100 questions server-side) rather than a fresh
// request per tweak.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api, { askosAuthHeader } from '../askosApi';
import { listPyq, attemptPyq, getPyqStats, getPyqPaperPdfUrl } from '../../api/pyqApi';
import BookmarkButton from '../BookmarkButton';
import PdfViewerModal from '../../components/Common/PdfViewerModal';

const PAPERS = ['Prelims', 'CSAT', 'GS1', 'GS2', 'GS3', 'GS4', 'Indian Language', 'English Language', 'GS Essay Paper', 'Optional Subjects'];
const ALL = '';

const PyqPracticePage = ({ slug }) => {
    const [subjects, setSubjects] = useState([]);
    const [subjectId, setSubjectId] = useState('');
    const [examType, setExamType] = useState('');
    const [sourceType, setSourceType] = useState(''); // '' | 'historical' | 'predicted'
    const [yearFilter, setYearFilter] = useState(ALL);
    const [specificPaper, setSpecificPaper] = useState(ALL);

    const [allQuestions, setAllQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [activeIdx, setActiveIdx] = useState(0);
    const [selected, setSelected] = useState(null);
    const [result, setResult] = useState(null);
    const [startedAt, setStartedAt] = useState(Date.now());
    const [pdfModal, setPdfModal] = useState({ show: false, url: null, title: '' });

    useEffect(() => {
        api.get(`/tenant/${slug}/askos/projects`, { headers: askosAuthHeader() })
            .then(res => setSubjects(res.data || []))
            .catch(() => setSubjects([]));
    }, [slug]);

    const load = useCallback(() => {
        setLoading(true);
        const params = {};
        if (subjectId) params.subject = subjectId;
        if (examType) params.exam_type = examType;
        if (sourceType) params.source_type = sourceType;
        listPyq(slug, params)
            .then(qs => { setAllQuestions(qs); setActiveIdx(0); setSelected(null); setResult(null); setStartedAt(Date.now()); })
            .catch(() => setAllQuestions([]))
            .finally(() => setLoading(false));
        getPyqStats(slug).then(setStats).catch(() => setStats(null));
    }, [slug, subjectId, examType, sourceType]);

    useEffect(() => { load(); }, [load]);

    // Reset narrower filters when the source-type or broader filters change —
    // years/papers only make sense for historical, and available options
    // shift with subject/exam-type anyway.
    useEffect(() => { setYearFilter(ALL); setSpecificPaper(ALL); }, [sourceType, subjectId, examType]);

    const availableYears = useMemo(
        () => Array.from(new Set(allQuestions.filter(q => q.exam_year).map(q => q.exam_year))).sort((a, b) => b - a),
        [allQuestions]
    );
    const availablePapers = useMemo(
        () => Array.from(new Set(allQuestions.filter(q => q.paper).map(q => q.paper))).sort(),
        [allQuestions]
    );

    const questions = useMemo(() => allQuestions.filter(q =>
        (yearFilter === ALL || q.exam_year === Number(yearFilter)) &&
        (specificPaper === ALL || q.paper === specificPaper)
    ), [allQuestions, yearFilter, specificPaper]);

    useEffect(() => { setActiveIdx(0); setSelected(null); setResult(null); }, [yearFilter, specificPaper]);

    const active = questions[activeIdx];

    const submit = async () => {
        if (!active) return;
        const time_spent_sec = Math.round((Date.now() - startedAt) / 1000);
        try {
            const res = await attemptPyq(slug, active.id, { selected_answer: selected, time_spent_sec });
            setResult(res);
            getPyqStats(slug).then(setStats).catch(() => {});
        } catch {
            setResult({ error: true });
        }
    };

    const goTo = (idx) => {
        setActiveIdx(Math.max(0, Math.min(idx, questions.length - 1)));
        setSelected(null);
        setResult(null);
        setStartedAt(Date.now());
    };
    const next = () => goTo(activeIdx + 1);
    const prev = () => goTo(activeIdx - 1);

    const openPdf = async () => {
        if (!active?.subject_project_id || !active?.paper) return;
        setPdfModal({ show: true, url: null, title: `${active.paper}${active.exam_year ? ` — ${active.exam_year}` : ''}` });
        try {
            const url = await getPyqPaperPdfUrl(slug, active.subject_project_id, active.paper, false);
            setPdfModal(m => ({ ...m, url }));
        } catch {
            setPdfModal({ show: false, url: null, title: '' });
        }
    };

    const downloadPdf = async () => {
        if (!active?.subject_project_id || !active?.paper) return;
        try {
            const url = await getPyqPaperPdfUrl(slug, active.subject_project_id, active.paper, true);
            const a = document.createElement('a');
            a.href = url; a.download = ''; document.body.appendChild(a); a.click(); a.remove();
        } catch {
            // no PDF available for this question's paper — silently ignore
        }
    };

    return (
        <div className="subpage">
            <h2>PYQ Practice</h2>

            {stats && stats.attempted > 0 && (
                <p className="empty-note" style={{ marginBottom: 16 }}>
                    You've attempted <strong>{stats.attempted}</strong> question{stats.attempted === 1 ? '' : 's'} ·{' '}
                    <strong>{stats.accuracy_pct}%</strong> accuracy
                </p>
            )}

            <div className="d-flex gap-2 mb-2 flex-wrap">
                <button type="button" className={!examType ? 'primary-btn' : 'secondary-btn'} onClick={() => setExamType('')}>All Types</button>
                {PAPERS.map(p => (
                    <button key={p} type="button" className={examType === p ? 'primary-btn' : 'secondary-btn'} onClick={() => setExamType(p)}>{p}</button>
                ))}
            </div>

            <div className="d-flex gap-2 mb-2 flex-wrap align-items-center">
                <select className="form-select form-select-sm" style={{ maxWidth: 320 }} value={subjectId} onChange={e => setSubjectId(e.target.value)}>
                    <option value="">All Subjects</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>

                <div className="d-flex gap-2">
                    <button type="button" className={!sourceType ? 'primary-btn' : 'secondary-btn'} onClick={() => setSourceType('')}>All</button>
                    <button type="button" className={sourceType === 'historical' ? 'primary-btn' : 'secondary-btn'} onClick={() => setSourceType('historical')}>Historical (Real PYQ)</button>
                    <button type="button" className={sourceType === 'predicted' ? 'primary-btn' : 'secondary-btn'} onClick={() => setSourceType('predicted')}>Predicted</button>
                </div>
            </div>

            {sourceType === 'historical' && (availableYears.length > 0 || availablePapers.length > 0) && (
                <div className="d-flex gap-2 mb-3 flex-wrap">
                    {availableYears.length > 0 && (
                        <select className="form-select form-select-sm" style={{ maxWidth: 160 }} value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
                            <option value={ALL}>All Years</option>
                            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    )}
                    {availablePapers.length > 0 && (
                        <select className="form-select form-select-sm" style={{ maxWidth: 260 }} value={specificPaper} onChange={e => setSpecificPaper(e.target.value)}>
                            <option value={ALL}>All Papers</option>
                            {availablePapers.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    )}
                </div>
            )}

            {loading && <p className="empty-note">Loading…</p>}
            {!loading && questions.length === 0 && <p className="empty-note">No PYQs available for this filter yet.</p>}

            {!loading && active && (
                <article className="panel">
                    <div className="panel-heading">
                        <h3>{active.paper || 'PYQ'} {active.exam_year ? `· ${active.exam_year}` : ''} {active.source_type === 'predicted' ? '· Predicted' : ''}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span>{activeIdx + 1} / {questions.length}</span>
                            <BookmarkButton slug={slug} target={{ pyq_question_id: active.id }} note={active.question_text?.slice(0, 200)} />
                        </div>
                    </div>

                    {active.source_type === 'historical' && (
                        <div className="d-flex gap-2 mb-3">
                            <button type="button" className="secondary-btn" onClick={openPdf}>📄 View Full Paper (PDF)</button>
                            <button type="button" className="secondary-btn" onClick={downloadPdf}>⬇ Download PDF</button>
                        </div>
                    )}

                    <p>{active.question_text}</p>

                    {active.options && (
                        <div className="d-flex flex-column gap-2 mb-3">
                            {Object.entries(active.options).map(([key, text]) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={selected === key ? 'primary-btn' : 'secondary-btn'}
                                    style={{ textAlign: 'left' }}
                                    disabled={!!result}
                                    onClick={() => setSelected(key)}
                                >
                                    <strong>{key}.</strong> {text}
                                </button>
                            ))}
                        </div>
                    )}
                    {!active.options && !result && (
                        <textarea
                            className="form-control mb-3"
                            rows={4}
                            placeholder="Write your answer (self-assessed against the model answer)..."
                            value={selected || ''}
                            onChange={e => setSelected(e.target.value)}
                        />
                    )}

                    {result && (
                        <div className="mb-3">
                            {result.is_correct !== null && (
                                <p style={{ color: result.is_correct ? '#1a7f37' : '#c0392b', fontWeight: 700 }}>
                                    {result.is_correct ? 'Correct!' : 'Not quite.'}
                                </p>
                            )}
                            <p className="empty-note"><strong>Answer:</strong> {result.answer_key}</p>
                            {result.explanation && <p className="empty-note"><strong>Explanation:</strong> {result.explanation}</p>}
                        </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center">
                        <button type="button" className="secondary-btn" onClick={prev} disabled={activeIdx === 0}>← Previous</button>
                        {!result && <button type="button" className="primary-btn" onClick={submit} disabled={!selected}>Submit</button>}
                        <button type="button" className="secondary-btn" onClick={next} disabled={activeIdx >= questions.length - 1}>
                            {result ? 'Next Question →' : 'Skip →'}
                        </button>
                    </div>
                </article>
            )}

            <PdfViewerModal
                show={pdfModal.show}
                url={pdfModal.url}
                title={pdfModal.title}
                onHide={() => setPdfModal({ show: false, url: null, title: '' })}
                onDownload={downloadPdf}
            />
        </div>
    );
};

export default PyqPracticePage;
